import type { ClientRect, CollisionDetection } from "@dnd-kit/core";
import { closestCenter, pointerWithin } from "@dnd-kit/core";
import { IFRAME_DATA_TEST_ID } from "../Frames/constants";
import { isDroppableData } from "./typePredicates";

export type CollisionArgs = Parameters<CollisionDetection>[0];

const isPointInsideRect = (point: { x: number; y: number }, rect: ClientRect) =>
  point.x >= rect.left &&
  point.x <= rect.right &&
  point.y >= rect.top &&
  point.y <= rect.bottom;

export interface PointerWithinIframeArgs extends CollisionArgs {}

export const stackOptimizedCollisionDetection: CollisionDetection = args => {
  const pointer = args.pointerCoordinates;
  if (!pointer) return [];

  const pointerWithinCollisions = pointerWithin(args);

  if (pointerWithinCollisions.length > 0) {
    return pointerWithinCollisions.sort((a, b) => {
      const aContainer = args.droppableContainers.find(
        container => container.id === a.id,
      );
      const bContainer = args.droppableContainers.find(
        container => container.id === b.id,
      );

      const aRect = aContainer?.rect.current;
      const bRect = bContainer?.rect.current;

      if (!aRect || !bRect) return 0;

      const aArea = (aRect.right - aRect.left) * (aRect.bottom - aRect.top);
      const bArea = (bRect.right - bRect.left) * (bRect.bottom - bRect.top);
      return aArea - bArea;
    });
  }

  return closestCenter(args);
};

export const pointerWithinIframe = ({
  pointerCoordinates,
  ...rest
}: PointerWithinIframeArgs) => {
  if (!pointerCoordinates) return [];

  const iframe = document.querySelector(
    `iframe[data-testid="${IFRAME_DATA_TEST_ID}"]`,
  );

  if (!iframe) {
    return pointerWithin({
      pointerCoordinates,
      ...rest,
    });
  }

  const iframeRect = iframe.getBoundingClientRect();

  const adjustedCoordinates = {
    x: pointerCoordinates.x - iframeRect.left,
    y: pointerCoordinates.y - iframeRect.top,
  };

  return pointerWithin({
    pointerCoordinates: adjustedCoordinates,
    ...rest,
  });
};

export const hybridCollisionDetection: CollisionDetection = args => {
  const pointer = args.pointerCoordinates;
  if (!pointer) return [];

  const floatingDroppables = Array.from(args.droppableContainers).filter(
    droppable =>
      isDroppableData(droppable.data.current) &&
      droppable.data.current.floatingDroppable,
  );

  const iframeDroppables = Array.from(args.droppableContainers).filter(
    droppable =>
      !isDroppableData(droppable.data.current) ||
      !droppable.data.current.floatingDroppable,
  );

  const floatingCollisions = stackOptimizedCollisionDetection({
    ...args,
    droppableContainers: floatingDroppables,
  });

  if (floatingCollisions.length > 0) {
    return floatingCollisions;
  }

  return pointerWithinIframe({
    ...args,
    droppableContainers: iframeDroppables,
  });
};

export const conditionalClosestCenter: CollisionDetection = args => {
  const canvasCard = document.querySelector(
    "[data-builder-ui-dnd-collision-detection-container]",
  );

  if (!args.pointerCoordinates || !canvasCard) return [];

  const canvasCardRect = canvasCard.getBoundingClientRect();
  if (!isPointInsideRect(args.pointerCoordinates, canvasCardRect)) {
    return [];
  }

  return hybridCollisionDetection(args);
};
